import { ErrorAlert } from "@components/ErrorAlert";
import {
  WSActions,
  WSForm,
  WSGrid,
  WSInfoBox,
  WSInputText,
  WSInputTextarea,
  WSList,
  WSPage,
  WSPageToolbar,
  WSSelect
} from "@wingspanhq/fe-component-library";
import { ImpersonationGrantLevel } from "@wingspanhq/wingspan-api-sdk";
import React from "react";
import { impersonationService } from "@services/impersonationService";
import { useWSMutation } from "../../../query/helpers";
import { analyticsService } from "@services/analyticsService";
import { PATH_DASHBOARD } from "../../Dashboard/paths";
import { FormDataImpersonate } from "../types";
import { validationSchemaImpersonate } from "../validationSchemaImpersonate";

const LEVEL_OPTIONS = [
  {
    value: ImpersonationGrantLevel.READ_ADMIN,
    label: "Read admin — read-only access"
  },
  {
    value: ImpersonationGrantLevel.FULL_ADMIN,
    label: "Full admin — read and write access"
  }
];

export const RouteImpersonate: React.FC = () => {
  const [impersonate, impersonateMeta] = useWSMutation<
    void,
    unknown,
    FormDataImpersonate
  >(async formData => {
    await impersonationService.start(formData);

    // Analytics must forget the admin identity before the impersonated one
    // arrives, and a full page load is what rebuilds every cache under the new
    // token — the impersonation survives it because it is persisted.
    analyticsService.reset();
    window.location.assign(PATH_DASHBOARD);
  });

  return (
    <WSPage>
      <WSPageToolbar title="Impersonate an account" />

      <WSGrid gutter="2XL">
        <WSGrid.Item span={{ s: "12", m: "8" }}>
          <WSForm<FormDataImpersonate>
            defaultValues={{
              targetAccountId: "",
              level: ImpersonationGrantLevel.READ_ADMIN,
              reason: ""
            }}
            validationSchema={validationSchemaImpersonate}
            onSubmit={impersonate}
          >
            <WSList gap="L">
              <WSInfoBox theme="neutral" transparent={false}>
                Creates an audited staff impersonation session for the target
                account. The session records who you are and the reason you
                give, and it expires on its own. Full admin access also requires
                recent multi-factor authentication.
              </WSInfoBox>

              <WSForm.Field
                name="targetAccountId"
                label="Target account ID"
                component={WSInputText}
                componentProps={{
                  placeholder: "e.g. t6yjwwQ2Ixa5uxp0ulZc6k",
                  required: true
                }}
              />

              <WSForm.Field
                name="level"
                label="Access level"
                component={WSSelect}
                componentProps={{
                  options: LEVEL_OPTIONS,
                  placeholder: "Select an access level",
                  required: true
                }}
              />

              <WSForm.Field
                name="reason"
                label="Reason"
                component={WSInputTextarea}
                componentProps={{
                  placeholder: "Why you need to impersonate this account",
                  required: true
                }}
              />

              <ErrorAlert
                context="StartImpersonation"
                error={impersonateMeta.error}
                forceShowRawError
              />

              <WSActions
                alignment="left"
                buttons={[
                  {
                    label: "Start impersonating",
                    type: "submit",
                    loading: impersonateMeta.isLoading
                  }
                ]}
              />
            </WSList>
          </WSForm>
        </WSGrid.Item>
      </WSGrid>
    </WSPage>
  );
};
